import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Cliente axios global — v9.74 auth via HttpOnly cookie + v9.77 refresh flow.
 *
 * Request:
 *   - `withCredentials: true` inclui os cookies `access_token` (60min) +
 *     `refresh_token` (7d) em toda request. Preview/produção da Emergent
 *     serve backend e frontend no mesmo host — cookies são first-party.
 *   - `X-Empresa-Id` vem de localStorage (preferência do usuário, não sensível).
 *
 * Response — v9.77:
 *   - 401 dispara UMA tentativa de `POST /auth/refresh` que troca o cookie
 *     `access_token` via `refresh_token`, e RETOMA a request original.
 *   - Se refresh também falhar → dispara evento `auth-logout` (capturado
 *     pelo AuthContext) que redireciona para /login e para pollers.
 *   - Evita que polling em background (notificações a cada 20s, notas presas
 *     a cada 90s, boletos a cada 30s) fique batendo 401 infinitamente.
 */
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const empresaId = localStorage.getItem("empresa_atual_id");
  if (empresaId) config.headers["X-Empresa-Id"] = empresaId;
  return config;
});

// Estado do refresh em flight — evita N chamadas paralelas de /auth/refresh
// quando 5 pollers batem 401 ao mesmo tempo.
let refreshInFlight = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;

    // Nunca tenta refresh na própria rota de refresh/login/logout (loop).
    const url = (original?.url || "").toLowerCase();
    const isAuthRoute =
      url.includes("/auth/refresh") ||
      url.includes("/auth/login") ||
      url.includes("/auth/logout");

    if (status === 401 && !isAuthRoute && !original._retry) {
      original._retry = true;
      try {
        if (!refreshInFlight) {
          refreshInFlight = api.post("/auth/refresh").finally(() => {
            // Timeout curto pra permitir batch de retries usarem o mesmo refresh
            setTimeout(() => { refreshInFlight = null; }, 500);
          });
        }
        await refreshInFlight;
        return api(original);
      } catch (refreshErr) {
        // Refresh falhou → sessão morta. Dispara logout global.
        window.dispatchEvent(new CustomEvent("auth-logout", {
          detail: { reason: "refresh-failed" }
        }));
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
